import { memo } from "react";
import { WORD_ID_ATTRIBUTE } from "../lib/viewport";
import type { StyleConfig } from "../state/styles";
import type { Word as WordType } from "../state/types";

interface WordProps {
  word: WordType;
  textStyle: StyleConfig["text"];
  /** True when the keyboard cursor is on this word. */
  isActive?: boolean;
}

/**
 * One word of the erasure.
 *
 * A word normally renders as a single span — the cheap path, and there are
 * thousands of these on a page. Only once the reader has carved individual
 * characters does it render per character.
 */
const Word = memo(({ word, textStyle, isActive = false }: WordProps) => {
  const { characters, customCharacters, isMarked, id } = word;

  return (
    <span
      id={`word-${id}`}
      {...{ [WORD_ID_ATTRIBUTE]: id }}
      role="option"
      aria-selected={isMarked}
      style={{
        ...textStyle.global,
        ...(customCharacters ? {} : isMarked ? textStyle.marked : textStyle.unmarked),
        padding: "0 .1em",
        display: "inline-block",
      }}
      // The cursor is drawn from a stylesheet rather than here, because it
      // depends on how the canvas was focused and inline styles cannot ask.
      className={`word ${isMarked ? "marked" : "unmarked"}${isActive ? " cursor" : ""}`}
    >
      {customCharacters
        ? characters.map((character) => (
            <span
              key={character.id}
              style={{
                ...textStyle.global,
                ...(character.isMarked ? textStyle.marked : textStyle.unmarked),
                display: "inline-block",
              }}
            >
              {character.character}
            </span>
          ))
        : characters.map((character) => character.character).join("")}
    </span>
  );
});

Word.displayName = "Word";

export default Word;
